import React, { useEffect, useRef, useState } from 'react'
import Layout from '../components/Layout.js'
import Button from '../components/Button.js'

const About = () => {
  const [activeSection, setActiveSection] = useState(null);
  const whatIsEffemRef = useRef(null);
  const podcastIndexRef = useRef(null);
  const atProtocolRef = useRef(null);
  const socialPodcastingRef = useRef(null);
  const yourDataRef = useRef(null);
  const openSourceRef = useRef(null);

  const sections = [
    { name: 'whatIsEffem', ref: whatIsEffemRef },
    { name: 'podcastIndex', ref: podcastIndexRef },
    { name: 'atProtocol', ref: atProtocolRef },
    { name: 'socialPodcasting', ref: socialPodcastingRef },
    { name: 'yourData', ref: yourDataRef },
    { name: 'openSource', ref: openSourceRef },
  ];

  const handleScroll = () => {
    const currentSection = sections.find(section => {
      const element = section.ref.current;
      if (element) {
        const { offsetTop } = element;
        const screenPosition = window.scrollY + window.innerHeight / 2;
        return screenPosition > offsetTop && element.getBoundingClientRect().bottom >= 50;
      }
      return false;
    });

    setActiveSection(currentSection ? currentSection.name : null);
  };

  const handleClick = (jumpToSectionRef) =>{
    jumpToSectionRef.current.scrollIntoView({ behavior: 'smooth' })
    const currentSection = sections.find(section =>{
      return section.ref === jumpToSectionRef;
    })
    setActiveSection(currentSection.name)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  const sidebarItem = (name, label, ref) => (
    <li
      className={`${
        activeSection === name
          ? "text-gray-900 dark:text-white"
          : "text-gray-600 dark:text-gray-500"
      } cursor-pointer my-6 text-base font-light hover:text-gray-900 dark:hover:text-white`}
      onClick={() => handleClick(ref)}
    >
      {label}
    </li>
  );

  const sectionHeading = (text) => (
    <h2 className="my-4 text-lg tracking-wider text-gray-600 dark:text-gray-500 md:text-xl lg:text-2xl">
      {text}
    </h2>
  );

  const sectionParagraph = (text) => (
    <p className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
      {text}
    </p>
  );

  return (
      <Layout title="About Effem" marginBottomClass={"mb-0"}>
          <section className="font-mono xl:px-12">
              <h1 className="text-gray-900 dark:text-white mx-auto mt-[50px] mb-[50px] text-[50px] tracking-wide max-md:text-[30px] max-md:mt-[30px] max-md:mb-[50px] md:max-lg:text-[30px]">
                  <span className="text-gradient from-purple-600 to-pink-600 pl-1.5 pr-1.5">
                  Social Podcasting via
                  </span>
                  <br />
                  <span className="text-gradient from-purple-600 to-pink-600 pl-1.5 pr-1.5">
                  Podcast Index & AT Protocol
                  </span>
              </h1>
              <div className="flex flex-col border-t border-gray-300 dark:border-gray-700 md:flex-row">
                  <div className=" hidden w-0 lg:h-full lg:sticky lg:top-[90px] lg:w-1/4 lg:justify-left lg:items-center lg:flex lg:flex-1 lg:text-2xl lg:mt-4">
                      <ul>
                          {sidebarItem('whatIsEffem', 'What is Effem?', whatIsEffemRef)}
                          {sidebarItem('podcastIndex', 'Podcast Index', podcastIndexRef)}
                          {sidebarItem('atProtocol', 'AT Protocol', atProtocolRef)}
                          {sidebarItem('socialPodcasting', 'Social Podcasting', socialPodcastingRef)}
                          {sidebarItem('yourData', 'Your Data, Your Identity', yourDataRef)}
                          {sidebarItem('openSource', 'Open Source', openSourceRef)}
                      </ul>
                  </div>
                  <div className="w-full pl-1.5 lg:border-l lg:border-gray-300 lg:dark:border-gray-700 lg:w-3/4 lg:pl-10">

                      <div ref={whatIsEffemRef} className="pt-16">
                          {sectionHeading('What is Effem?')}
                          {sectionParagraph(
                            'Effem is a podcast player for iOS that puts open standards and social interaction at the center of the listening experience. ' +
                            'Rather than relying on proprietary platforms that control what you can discover and how you interact with content, ' +
                            'Effem is built on top of the Podcast Index and the AT Protocol — two open technologies that keep podcasting free and give listeners real ownership over their data.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'With Effem, you can search and discover podcasts from across the open podcasting ecosystem, subscribe to your favorite shows, ' +
                            'download episodes for offline listening, and manage your queue — all the things you expect from a great podcast player. ' +
                            'But Effem goes further by weaving social features directly into the experience: comment on episodes, share your subscriptions, ' +
                            'and connect with other listeners, all powered by your decentralized AT Protocol identity.'
                          )}
                      </div>

                      <div ref={podcastIndexRef} className="pt-16">
                          {sectionHeading('Podcast Index')}
                          {sectionParagraph(
                            'The Podcast Index is an open, freely available podcast directory that exists to keep the podcasting ecosystem independent and censorship-resistant. ' +
                            'Unlike closed platforms that act as gatekeepers to podcast discovery, the Podcast Index is a community-driven project ' +
                            'that catalogs millions of podcasts and makes them accessible to any app or service — no permission required.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'Effem uses the Podcast Index to power its search and discovery features. When you search for a podcast in Effem, ' +
                            'you are searching the entire open podcasting ecosystem — not a curated subset controlled by a single company. ' +
                            'This means independent creators, niche shows, and new voices all get the same visibility as established networks.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'The Podcast Index also supports modern podcasting features like chapters, transcripts, and value-for-value payments, ' +
                            'helping to push the medium forward while keeping it open for everyone.'
                          )}
                      </div>

                      <div ref={atProtocolRef} className="pt-16">
                          {sectionHeading('AT Protocol')}
                          {sectionParagraph(
                            'The AT Protocol (Authenticated Transfer Protocol) is a decentralized networking protocol originally created for social applications. ' +
                            'It provides portable user identities, decentralized data storage, and an open ecosystem where different services can interoperate ' +
                            'without being locked into a single platform.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'You may already know the AT Protocol through Bluesky, the social network built on top of it. ' +
                            'Effem extends the AT Protocol beyond microblogging and into podcasting. ' +
                            'By building on AT Proto, Effem gives you a portable identity — your handle and your data travel with you. ' +
                            'If you already have a Bluesky account, you can sign into Effem with the same identity.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'Effem runs its own custom AppView — a service in the AT Protocol architecture that reads and serves data for a specific application. ' +
                            'This AppView handles podcast subscriptions, episode states, comments, and other social data, ' +
                            'all stored in a way that respects the decentralized nature of the protocol.'
                          )}
                      </div>

                      <div ref={socialPodcastingRef} className="pt-16">
                          {sectionHeading('Social Podcasting')}
                          {sectionParagraph(
                            'Podcasting has always been a shared experience — people recommend shows to friends, discuss episodes at work, ' +
                            'and bond over favorite moments. Effem brings that social layer directly into the app, so you do not have to leave your player to participate.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'Comment on episodes to share your thoughts, reactions, and recommendations with other listeners. ' +
                            'Your comments are tied to your AT Protocol identity, so they are portable and owned by you — not locked inside a single app. ' +
                            'See what others are saying about the episodes you are listening to, and discover new perspectives on your favorite shows.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'Your podcast subscriptions sync through the AT Protocol, meaning your listening profile is part of the decentralized network. ' +
                            'Bookmark episodes you want to revisit, track your listening progress across devices, and build playlists that fit the way you listen. ' +
                            'Social podcasting is not about broadcasting everything you do — it is about giving you the tools to connect when you want to.'
                          )}
                      </div>

                      <div ref={yourDataRef} className="pt-16">
                          {sectionHeading('Your Data, Your Identity')}
                          {sectionParagraph(
                            'Most podcast apps keep your subscriptions, listening history, and preferences locked inside their ecosystem. ' +
                            'Switch apps, and you start over. Effem takes a different approach.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'Because Effem is built on the AT Protocol, your identity is yours. Your handle, your social graph, and your data ' +
                            'are not trapped in a proprietary silo. If a better podcast app comes along tomorrow, your data can move with you. ' +
                            'That is the promise of decentralized protocols — no single company holds the keys to your digital life.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'This also means Effem does not need to build a walled garden to keep you around. ' +
                            'The app earns your attention by being genuinely useful, not by holding your data hostage. ' +
                            'We think that is how software should work.'
                          )}
                      </div>

                      <div ref={openSourceRef} className="pt-16">
                          {sectionHeading('Open Source')}
                          {sectionParagraph(
                            'Effem is built on open standards and contributes back to the open-source community. ' +
                            'The Podcast Index, the AT Protocol, and the libraries that connect them are all open projects, ' +
                            'and Effem is committed to supporting and advancing that ecosystem.'
                          )}
                          <div className="mt-4" />
                          {sectionParagraph(
                            'The AppView that powers Effem\'s social features, the Swift packages that integrate with the AT Protocol, ' +
                            'and the tools built along the way are developed in the open. ' +
                            'You can explore the code, report issues, and contribute on Tangled.'
                          )}
                          <div className="mt-4" />
                          <a
                            href="https://tangled.org/sparrowtek.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block text-[12px] lg:text-[14px] text-purple-600 dark:text-purple-400 tracking-wider font-light hover:text-pink-600 dark:hover:text-pink-400 transition-colors duration-300"
                          >
                            Visit SparrowTek on Tangled &rarr;
                          </a>
                      </div>

                      <div className="pt-16 pb-24">
                          <Button
                              text="Contact Us"
                              link="/contact"
                              variant="gradient"
                              shadow={true}
                          />
                      </div>
                  </div>
              </div>
          </section>
      </Layout>
  );
}

export default About
